import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { PlayCircle, BookOpen, FileText, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import { db } from '@/lib/firebase';
import { collection, getDocs, addDoc } from 'firebase/firestore';
import { defaultResources } from '@/lib/defaults';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

export default function Resources() {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadResources = async () => {
      try {
        const resSnap = await getDocs(collection(db, 'resources'));
        if (resSnap.empty) {
          // Seed default resources if none exist
          for (const { id: _id, ...r } of defaultResources) {
            await addDoc(collection(db, 'resources'), { ...r, createdAt: new Date().toISOString() });
          }
          const reloadSnap = await getDocs(collection(db, 'resources'));
          setResources(reloadSnap.docs.map(d => ({ ...d.data(), id: d.id })));
        } else {
          setResources(resSnap.docs.map(d => ({ ...d.data(), id: d.id })));
        }
      } catch (e) {
        console.error('Failed to load resources:', e);
        setResources(defaultResources);
      }
      setLoading(false);
    };
    loadResources();
  }, []);

  // Sort by order if available
  const sortedResources = [...resources].sort((a, b) => (a.order || 99) - (b.order || 99));

  return (
    <div data-testid="resources-page" className="bg-white">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <motion.div initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.5 }} className="max-w-3xl mb-16">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#F5F0F7] text-[#4B1D5E] text-sm font-medium mb-6">Resources</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#2D1B36] mb-6">Helpful Resources</h1>
          <p className="text-base sm:text-lg text-[#5E4B66]">
            Videos, articles, and tips to support your singing journey between lessons.
          </p>
        </motion.div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4B1D5E]"></div>
          </div>
        ) : sortedResources.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[#8A7E90]">No resources available yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {sortedResources.map((r, i) => (
              <motion.div key={r.id} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }}>
                <Card className="bg-white border border-purple-100 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 h-full">
                  <CardContent className="p-0 flex flex-col h-full">
                    {r.type === 'video' && r.url ? (
                      <div className="aspect-video rounded-t-2xl overflow-hidden bg-gray-100">
                        <iframe
                          src={r.url}
                          title={r.title}
                          className="w-full h-full"
                          allowFullScreen
                          loading="lazy"
                          data-testid={`resource-video-${r.id}`}
                        />
                      </div>
                    ) : (
                      <div className="aspect-video rounded-t-2xl bg-[#F5F0F7] flex items-center justify-center">
                        {r.type === 'article' ? (
                          <BookOpen className="h-12 w-12 text-[#B088BC]" strokeWidth={1} />
                        ) : r.type === 'link' ? (
                          <ExternalLink className="h-12 w-12 text-[#B088BC]" strokeWidth={1} />
                        ) : (
                          <FileText className="h-12 w-12 text-[#B088BC]" strokeWidth={1} />
                        )}
                      </div>
                    )}
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        {r.type === 'video' ? (
                          <PlayCircle className="h-4 w-4 text-[#4B1D5E]" />
                        ) : r.type === 'link' ? (
                          <ExternalLink className="h-4 w-4 text-[#4B1D5E]" />
                        ) : (
                          <BookOpen className="h-4 w-4 text-[#4B1D5E]" />
                        )}
                        <span className="text-xs font-medium text-[#4B1D5E] uppercase tracking-wider">
                          {r.type}
                        </span>
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-[#2D1B36] mb-2">{r.title}</h3>
                      <p className="text-sm text-[#5E4B66] leading-relaxed flex-1">{r.description}</p>
                      {r.type === 'link' && r.url && (
                        <a
                          href={r.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[#4B1D5E] hover:text-[#3A164A] transition-colors"
                          data-testid={`resource-link-${r.id}`}
                        >
                          Visit Link <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        <div className="mt-16 text-center">
          <p className="text-[#8A7E90] text-sm">More resources are added regularly. Check back soon!</p>
        </div>
      </section>
    </div>
  );
}
